import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Button } from 'react-bootstrap';

const containerStyles = {
  maxWidth: '800px',
  margin: '50px auto',
  padding: '20px'
};

const technicalDetailsStyles = {
  maxHeight: '300px',
  marginTop: '10px',
  padding: '12px',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  backgroundColor: '#f8f9fa',
  border: '1px solid #dee2e6',
  borderRadius: '4px',
  fontFamily: 'monospace',
  fontSize: '12px'
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Unhandled React rendering error', {
      error,
      componentStack: errorInfo.componentStack
    });

    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleResetApplication = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    } catch (error) {
      console.error('Unable to clear browser storage', error);
    }
  };

  handleGoToLogin = () => {
    window.location.assign('/login');
  };

  getTechnicalInformation = () => {
    const { error, errorInfo } = this.state;

    return {
      message: error?.message ?? 'Unknown error',
      name: error?.name ?? 'Error',
      stack: error?.stack ?? 'Not available',
      componentStack: errorInfo?.componentStack ?? 'Not available',
      browser: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        localStorageItems: localStorage.length,
        sessionStorageItems: sessionStorage.length
      }
    };
  };

  renderErrorDetails() {
    if (process.env.NODE_ENV === 'production') {
      return null;
    }

    const technicalInformation = this.getTechnicalInformation();

    return (
      <details className="mt-4">
        <summary className="fw-semibold" style={{ cursor: 'pointer' }}>
          Technical details
        </summary>

        <pre style={technicalDetailsStyles}>
          {JSON.stringify(technicalInformation, null, 2)}
        </pre>
      </details>
    );
  }

  renderFallback() {
    return (
      <main style={containerStyles}>
        <Alert variant="danger">
          <Alert.Heading>Application error</Alert.Heading>

          <p className="mb-0">
            An unexpected error occurred while rendering the application.
            Reload the page or reset the locally stored application data.
          </p>
        </Alert>

        <section className="mt-4">
          <h2 className="h5">Recovery actions</h2>

          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button variant="primary" onClick={this.handleReload}>
              Reload page
            </Button>

            <Button variant="outline-danger" onClick={this.handleResetApplication}>
              Clear local data and reload
            </Button>

            <Button variant="outline-secondary" onClick={this.handleGoToLogin}>
              Go to login
            </Button>
          </div>
        </section>

        {this.renderErrorDetails()}

        <Alert variant="secondary" className="mt-4 mb-0">
          If the error continues, contact technical support and include the
          current URL, the approximate time of the error and the action that
          triggered it.
        </Alert>
      </main>
    );
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;

    return hasError ? this.renderFallback() : children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary;